import URI from 'urijs';
import AgGridPagination from '../components/ag_grid_pagination';
import AgGridColumnFilters from '../components/ag_grid_column_filters';
import * as infiniteHits from '../../search/load_more_results';
import { showModalFromUrl, initializeSaveSearch } from '../../search/search_helpers';
import { safeAddWidgets } from '../../search/base_widgets';
window.IndexSnapshotSaver = require('../components/index_snapshot_saver');

window.WorkplaceProjects = {
  _projectSearch: null,
  _gridOptions: null,
  _currentDataSize: 0,
  _initCall: true,

  _phaseUrl(data) {
    if (_.isEqual(data.roles, ['bidder']) || _.includes(data.roles, 'evaluator')) { return this._projectTendersUrl(data); }
    if (_.includes(data.roles, 'auditor') || (_.includes(data.roles, 'verifier') && (data.document_set_status === 'draft'))) { return this._phaseOverviewUrl(data); }
    if (!!data.documents_statuses && !data.prequalification) { return this._documentSetUrl(data); }
    return `/workplace/projects/${data.project_slug}/phases/${data.phase_slug}`;
  },

  _phaseOverviewUrl(data) {
    return `/workplace/projects/${data.project_slug}/phases/${data.phase_slug}`;
  },

  _projectTendersUrl(data) {
    return `/workplace/projects/${data.project_slug}/phases/${data.phase_slug}/tenders`;
  },

  _documentSetUrl(data) {
    return `/workplace/projects/${data.project_slug}/phases/${data.phase_slug}/document_sets`;
  },

  _issuesUrl(data) {
    return `/workplace/projects/${data.project_slug}/phases/${data.phase_slug}/document_version_issues`;
  },

  _projectNameRenderer(row) {
    return `<a href='${this._phaseUrl(row.data)}'><b>${row.data.title}</b></a>`;
  },

  _documentSetStatusRenderer(row) {
    if (row.data.document_set_formatted_status) {
      return `<div class='status-label document-status ${row.data.document_set_status}'>` +
        row.data.document_set_formatted_status +
        '</div>';
    } else {
      return '';
    }
  },

  _documentSetVersionRenderer(row) {
    if (!row.data.document_set_version) { return ''; }
    return `<a href='${this._documentSetUrl(row.data)}'><b>V${row.data.document_set_version}</b></a>`;
  },

  _issuesRenderer(data) {
    let str = `<a class='data-item' href='${this._issuesUrl(data)}'>`;
    for (let key in data.issues) {
      const value = data.issues[key];
      str += `<span class='data-count'>${value} ${WorkplaceUtils.capitalize(key)}</span>`;
    }
    return str += '</a>';
  },

  _issuesListRenderer(data) {
    if (_.isEmpty(data.issues)) { return ''; }
    let str = ``;
    for (let key in data.issues) {
      const value = data.issues[key];
      str += `<div class='data-count'>${value} ${WorkplaceUtils.capitalize(key)}</div>`;
    }
    return str += '';
  },

  _documentsRenderer(row) {
    let str = '<div class="data-item">';
    for (let key in row.data.documents_statuses) {
      const value = row.data.documents_statuses[key];
      const status = I18n.t('activerecord.state_machines.document_version.states.' + key);
      str += `<div class='data-count'>${value} ${status}</div>`;
    }
    return str += '</div>';
  },

  _documentsListRenderer(data) {
    let str = '';
    for (let key in data.documents_statuses) {
      const value = data.documents_statuses[key];
      const status = I18n.t('activerecord.state_machines.document_version.states.' + key);
      str += `<div class='data-count'>${value} ${status}</div>`;
    }
    return str;
  },

  _dateRenderer(row) {
    if (row.data.document_set_status_changed_at) {
      const status = I18n.t('activerecord.state_machines.document_set.states.' + row.data.document_set_status);
      if (row.data.document_set_status === 'under_review') {
        return `<div>${status} since ${row.data.document_set_status_changed_at}</div>`;
      } else {
        return `<div>${status} on ${row.data.document_set_status_changed_at}</div>`;
      }
    } else {
      return '';
    }
  },

  _phaseRenderer(data) {
    return WorkplaceUtils.capitalizeAllWords(data.value);
  },

  _documentSetStatusComparator(valueA, valueB, nodeA, nodeB) {
    return WorkplaceUtils.compareNodeStrings(valueA, valueB, nodeA, nodeB, 'document_set_status');
  },

  _updatedAtDateComparator(valueA, valueB, nodeA, nodeB) {
    return nodeA.data['updated_at'] - nodeB.data['updated_at'];
  },

  _statusChangedAtDateComparator(valueA, valueB, nodeA, nodeB) {
    const nodeAIntDate = new Date(nodeA.data['document_set_status_changed_at']).getTime();
    const nodeBIntDate = new Date(nodeB.data['document_set_status_changed_at']).getTime();
    return nodeAIntDate - nodeBIntDate;
  },

  _countHashValues(obj) {
    const arr = _.map(obj, (v, k) => v);
    return _.reduce(arr, ((mem, val) => mem + val), 0);
  },

  _issuesComparator(valueA, valueB, nodeA, nodeB) {
    return this._countHashValues(valueA) - this._countHashValues(valueB);
  },

  _documentsComarator(valueA, valueB, nodeA, nodeB) {
    return this._countHashValues(nodeA.data.documents_statuses) - this._countHashValues(nodeB.data.documents_statuses);
  },

  _projectNameComparator(valueA, valueB, nodeA, nodeB) {
    return WorkplaceUtils.compareNodeStrings(valueA, valueB, nodeA, nodeB, 'title');
  },

  _resizeTable() {
    return resizeAgGridTable('#projects-table', { api: this._gridApi }, 1, this._currentDataSize, 48);
  },

  _initializeCookieSavers(preloadedCookies, path) {
    const cookieNames = [
      'projects-table-column-filters',
      'projects-table-column-order'
    ];

    return IndexSnapshotSaver.initialize(cookieNames, preloadedCookies, path).startSaving();
  },

  _initializeWidgets() {
    const widgets = [
      () => Widgets.statsWidget(),
      () => WorkplaceWidgets.descriptionWidget(),
      () => Widgets.currentRefinedValuesWidget()({ excludedAttributes: [] }),
      () => Widgets.searchBoxWidget(I18n.t('v4.projects.filters.enter_keyword')),
      () => WorkplaceWidgets.locationWidget(),
      () => WorkplaceWidgets.sectorsWidget(),
      () => WorkplaceWidgets.auditorsWidget(),
      () => WorkplaceWidgets.developersWidget(),
      () => WorkplaceWidgets.ownersWidget(),
      () => WorkplaceWidgets.verifiersWidget(),
      () => Widgets.clearAllWidget(),
      () => Widgets.saveSearchWidget()
    ];

    if ($('#phase').length > 0) { widgets.push(() => WorkplaceWidgets.phaseWidget()); }
    if ($('#doc-set-status').length > 0) { widgets.push(() => WorkplaceWidgets.docSetStatusWidget()); }
    if ($('#open-doc-set-issues').length > 0) { widgets.push(() => WorkplaceWidgets.hasOpenDocumentIssuesWidget()); }

    safeAddWidgets(this._projectSearch, widgets);
  },

  _initializeSaveSearch() {
    initializeSaveSearch({
      type: 'projects',
      editSelector: '#add-drop a.edit',
      showSignInModal: false
    });
  },

  initializeTable(app_id, key, index, preloadedCookies) {
    if (preloadedCookies == null) { preloadedCookies = []; }
    this._projectSearch = SearchUtils.createInstantSearch(app_id, key, index);

    this._projectSearch.addWidgets([
      instantsearch.widgets.configure({ hitsPerPage: 20000 }),
      WorkplaceWidgets.projectsHitsWidget()({})
    ]);
    this._initializeWidgets();
    this._projectSearch.start();
    this._initializeSaveSearch();

    const containerWidth = $('.content-wrapper.search-results-container').width();
    const minProjectGroupWidth = containerWidth * 0.5;
    const minDocumentSetGroupWidth = containerWidth * 0.4;
    const columnDefs =
      [
        {
          headerName: I18n.t('v4.projects.headers.project'),
          children: [
            { headerName: I18n.t('v4.projects.headers.project_name'), tooltipField: 'title', width: 450, minWidth: minProjectGroupWidth * 0.2, cellRenderer: _.bind(this._projectNameRenderer, this), comparator: _.bind(this._projectNameComparator, this) },
            { headerName: I18n.t('v4.projects.headers.phase'), field: 'phase', minWidth: minProjectGroupWidth * 0.2, cellRenderer: _.bind(this._phaseRenderer, this) },
            { headerName: I18n.t('v4.projects.headers.ref_no'), width: 200, minWidth: minProjectGroupWidth * 0.1, field: 'reference' },
            { headerName: I18n.t('v4.projects.headers.consultant'), width: 200, minWidth: minProjectGroupWidth * 0.1, field: 'documenters' },
            { headerName: I18n.t('v4.projects.headers.sectors'), width: 200, minWidth: minProjectGroupWidth * 0.1, field: 'sectors' },
            { headerName: I18n.t('v4.projects.headers.location'), width: 250, minWidth: minProjectGroupWidth * 0.15, field: 'location_text' },
            { headerName: 'Update', width: 150, minWidth: minProjectGroupWidth * 0.15, field: 'formatted_updated_at', comparator: _.bind(this._updatedAtDateComparator, this) }
          ]
        },
        {
          headerName: I18n.t('v4.projects.headers.document_set'),
          children: [
            { headerName: 'Status', width: 200, minWidth: minDocumentSetGroupWidth * 0.2, cellRenderer: _.bind(this._documentSetStatusRenderer, this), comparator: _.bind(this._documentSetStatusComparator, this) },
            { headerName: I18n.t('v4.projects.headers.version'), field: 'document_set_version', width: 200, minWidth: minDocumentSetGroupWidth * 0.1, cellRenderer: _.bind(this._documentSetVersionRenderer, this) },
            { headerName: I18n.t('v4.projects.headers.documents'), width: 450, minWidth: minDocumentSetGroupWidth * 0.3, cellRenderer: this._documentsRenderer, comparator: _.bind(this._documentsComarator, this) },
            { headerName: 'Update', width: 350, minWidth: minDocumentSetGroupWidth * 0.2, cellRenderer: this._dateRenderer, comparator: _.bind(this._statusChangedAtDateComparator, this) },
            { headerName: I18n.t('v4.projects.headers.issues'), field: 'issues', minWidth: minDocumentSetGroupWidth * 0.2, cellRenderer: (row => this._issuesRenderer(row.data)), comparator: _.bind(this._issuesComparator, this) },
          ]
        }
      ];

    this._gridOptions = {
      paginationPageSize: 48,
      headerHeight: 44,
      rowHeight: 44,
      pagination: true,
      suppressPaginationPanel: true,
      columnDefs,
      defaultColDef: {
        sortable: true,
        resizable: true
      },
      onGridReady(e) {
        return e.api.sizeColumnsToFit();
      },
      onGridSizeChanged(event) {
        return event.api.sizeColumnsToFit();
      }
    };
    const agGridPagination = AgGridPagination.initialize(this._gridOptions);
    this._gridOptions.onPaginationChanged = agGridPagination.renderPagination.bind(agGridPagination);

    $('#pagination').exists().on('click', () => {
      var pageSize = this._gridApi.paginationGetPageSize();
      var currentPage = this._gridApi.paginationGetCurrentPage();
      var totalRows = this._gridApi.paginationGetRowCount();
      this._currentDataSize = Math.min(pageSize, totalRows - (currentPage * pageSize));
      return this._resizeTable();
    });

    const gridDiv = document.getElementById('projects-table');
    // v35: createGrid replaces new agGrid.Grid()
    this._gridApi = agGrid.createGrid(gridDiv, this._gridOptions);
    // Store api on gridOptions for backward compatibility with shared components
    this._gridOptions.api = this._gridApi;
    this._initializeCookieSavers(preloadedCookies, '/workplace');
    AgGridColumnFilters.initialize(this._gridOptions, [], [], []);
    return AgGridPagination.initializePaginationListeners();
  },

  initializeList(app_id, key, index) {
    this._projectSearch = SearchUtils.createInstantSearch(app_id, key, index);

    this._projectSearch.addWidgets([this._projectsListHitsWidget()]);
    this._initializeWidgets();
    this._projectSearch.start();
    infiniteHits.loadMoreResults();
  },

  _projectsListHitsWidget() {
    return instantsearch.widgets.infiniteHits({
      container: '#projects-grid',
      templates: {
        item: $('#project-template').text(),
        empty: $('#no-search-results-found-template').text()
      },
      cssClasses: {
        root: 'ais-Hits',
        item: 'item',
        list: 'list-view grid items borders ais-Hits-list'
      },
      transformItems: items => {
        items.map(item => {
          item.path = this._phaseUrl(item);
          item.documents = this._documentsListRenderer(item);
          item.formatted_issues = this._issuesListRenderer(item);
          return item;
        });

        return items;
      }
    });
  },

  emptyCallHandler() {
    if (this._initCall) {
      $('.search-results-container').hide();
      $('#spinner').hide();
      return $('.empty-page').removeClass('hidden');
    }
  },

  reloadData(data) {
    this._initCall = false;
    this._gridApi.setGridOption('rowData', data);
    $('#projects-grid').parent().removeClass('hidden');
    $('.actions').removeClass('hidden');
    $('#spinner').hide();
    return this._resizeTable();
  },

  initializeCSVExport() {
    const _this = this;
    return $('#export-projects-csv').click(function (e) {
      e.preventDefault();
      const algoliaQuery = _this._projectSearch.helper.getQuery();
      return $.pseudoAjax({
        url: $(this).attr('href'),
        key: 'algolia_query',
        data: JSON.stringify(algoliaQuery)
      });
    });
  }
};
